/**
 * Cliente API de los Reportes de Tesorería (Sprint 20).
 *
 * Consume /tesoreria/reportes/* — reportería financiera "al vuelo" anclada al
 * año contable activo. Los endpoints `/export` devuelven un XLSX (se descargan
 * con `descargarArchivo`, no con `apiFetch` que parsea JSON).
 */
import { apiFetch, descargarArchivo } from './client';

// === SR-03 Deudores ===

export interface DeudorReporte {
  lote_id: string;
  identificador: string;
  sector: string | null;
  titular_nombre: string | null;
  titular_celular: string | null;
  saldo_a_hoy: number;
  saldo_total_pendiente: number;
  saldo_semestre_actual: number;
  vence_el_mas_antiguo: string | null;
  antiguedad_dias: number | null;
}

export interface ReporteDeudoresResponse {
  anio: number;
  total_deudores: number;
  total_saldo_a_hoy: number;
  deudores: DeudorReporte[];
}

// === Estado de morosidad (nivel handoff) ===

export interface CasaMorosa {
  lote_id: string;
  identificador: string;
  titular_nombre: string | null;
  meses_mora: number;
  ultimo_pago: string | null;
  saldo_a_hoy: number;
}

export interface BucketMora {
  id: string;
  label: string;
  descripcion: string;
  color: string;
  casas: number;
  monto: number;
}

export interface PuntoEvolucionMora {
  anio: number;
  mes: number;
  monto: number;
}

export interface ReporteMorosidadResponse {
  total_mora: number;
  casas_morosas: number;
  total_casas: number;
  cartera_semestral: number;
  deuda_promedio: number;
  pct_cartera: number | null;
  buckets: BucketMora[];
  evolucion: PuntoEvolucionMora[];
  casas: CasaMorosa[];
}

export function getReporteMorosidad(): Promise<ReporteMorosidadResponse> {
  return apiFetch<ReporteMorosidadResponse>('/tesoreria/reportes/morosidad');
}

export function descargarMorosidadXlsx(): Promise<void> {
  return descargarArchivo(
    '/tesoreria/reportes/morosidad/export',
    'morosidad.xlsx',
  );
}

// === Recaudación por casa ===

export interface CasaRecaudacion {
  lote_id: string;
  identificador: string;
  sector: string | null;
  titular_nombre: string | null;
  estado: string;
  cuotas_pagadas: number;
  recaudado_barrio: number;
  aportado_terceros: number;
}

export interface ReporteRecaudacionPorCasaResponse {
  anio: number;
  total_recaudado_barrio: number;
  total_terceros: number;
  total_casas: number;
  meses_transcurridos: number;
  esperado: number;
  cumplimiento: number | null;
  casas_al_dia: number;
  casas: CasaRecaudacion[];
}

// === Conciliación bancaria (reenfoque → saldos_bancarios) ===

export interface DapVigenteReporte {
  id: string;
  banco: string;
  capital: number;
  fecha_apertura: string;
  fecha_vencimiento: string;
  tasa_bps: number | null;
  folio: string | null;
}

export interface MovimientosDesdeCuadre {
  ingresos: number;
  gastos: number;
  traspasos_dap: number;
}

export interface CuadreHistorico {
  fecha_registro: string;
  hora_registro: string;
  saldo_banco: number;
  saldo_calculado: number;
  diferencia: number;
  notas: string | null;
}

export interface ReporteConciliacionResponse {
  tiene_cuadre: boolean;
  ultimo_cuadre_fecha: string | null;
  saldo_banco: number | null;
  saldo_calculado: number | null;
  diferencia: number | null;
  capital_dap_vigente: number;
  posicion_total: number | null;
  saldo_calculado_hoy: number | null;
  movimientos_desde_cuadre: MovimientosDesdeCuadre;
  daps_vigentes: DapVigenteReporte[];
  historico: CuadreHistorico[];
}

export function getReporteConciliacion(): Promise<ReporteConciliacionResponse> {
  return apiFetch<ReporteConciliacionResponse>(
    '/tesoreria/reportes/conciliacion',
  );
}

// === Análisis de proveedores ===

export interface ProveedorReporte {
  proveedor: string;
  total: number;
  pct: number | null;
  n_operaciones: number;
  meses_activos: number;
  categoria_predominante: string | null;
  ticket_promedio: number;
  recurrente: boolean;
}

export interface GrupoProveedores {
  n: number;
  monto: number;
}

export interface ReporteProveedoresResponse {
  anio: number;
  total_gastado: number;
  n_proveedores: number;
  concentracion_top3: number | null;
  ticket_promedio: number;
  recurrentes: GrupoProveedores;
  puntuales: GrupoProveedores;
  proveedores: ProveedorReporte[];
}

export function getReporteProveedores(
  anio?: number,
  incluirComp?: boolean,
): Promise<ReporteProveedoresResponse> {
  return apiFetch<ReporteProveedoresResponse>(
    `/tesoreria/reportes/proveedores${qsAnioComp(anio, incluirComp)}`,
  );
}

export function descargarProveedoresXlsx(
  anio?: number,
  incluirComp?: boolean,
): Promise<void> {
  return descargarArchivo(
    `/tesoreria/reportes/proveedores/export${qsAnioComp(anio, incluirComp)}`,
    'proveedores.xlsx',
  );
}

// === Cierre mensual ===

export interface ItemComposicion {
  tipo: string;
  monto: number;
  color: string | null;
}

export interface ReporteCierreMensualResponse {
  anio: number;
  mes: number;
  periodo: string;
  ingresos: number;
  gastos: number;
  resultado: number;
  trend_ingresos: number | null;
  trend_gastos: number | null;
  trend_resultado: number | null;
  /** OJO: NO es el saldo del último día del mes. Es un snapshot manual que el
   *  tesorero registra cuando revisa la cuenta; la fecha real va aparte. */
  saldo_cierre: number | null;
  saldo_cierre_fecha: string | null;
  saldo_apertura: number | null;
  saldo_apertura_fecha: string | null;
  casas_que_pagaron: number;
  total_casas: number;
  pagos_del_mes: number;
  nuevos_morosos: number | null;
  compensaciones_n: number;
  compensaciones_monto: number;
  ingresos_por_tipo: ItemComposicion[];
  gastos_por_categoria: ItemComposicion[];
}

// v1: sin selector de mes — el backend resuelve el último mes cerrado.
export function getReporteCierreMensual(
  incluirComp?: boolean,
): Promise<ReporteCierreMensualResponse> {
  return apiFetch<ReporteCierreMensualResponse>(
    `/tesoreria/reportes/cierre-mensual${qsAnioComp(undefined, incluirComp)}`,
  );
}

export function descargarCierreMensualXlsx(
  _anio?: number,
  incluirComp?: boolean,
): Promise<void> {
  return descargarArchivo(
    `/tesoreria/reportes/cierre-mensual/export${qsAnioComp(undefined, incluirComp)}`,
    'cierre-mensual.xlsx',
  );
}

// === Informe mensual a los vecinos (2026-09-03) ===

export interface MesGastoVentana {
  anio: number;
  mes: number;
  periodo: string; // AAAA-MM
  monto: number;
  /** `sistema` | `historico` | `sin_datos` — de dónde salió el dato del mes. */
  fuente: string;
}

export interface PromedioGastoMovil {
  desde: string;
  hasta: string;
  meses_ventana: number;
  total: number;
  promedio: number;
  mes_minimo: number;
  mes_maximo: number;
  meses_sin_datos: number;
  usa_historico: boolean;
  meses: MesGastoVentana[];
}

export interface ResumenDestinatariosInforme {
  total_casas: number;
  titulares_con_correo: number;
  casas_sin_correo: number;
  identificadores_sin_correo: string[];
  /** Titulares que YA recibieron el informe de este período. */
  ya_enviados: number;
}

export interface InformeMensualVecinosResponse {
  anio: number;
  mes: number;
  periodo: string;
  asunto: string;
  cierre: ReporteCierreMensualResponse;
  proyeccion: PromedioGastoMovil;
  cobertura_meses: number | null;
  /** Fecha real del snapshot de saldo usado (registro manual, no fin de mes). */
  saldo_fecha: string | null;
  /** Fecha del snapshot de apertura (el saldo del mes anterior). */
  saldo_fecha_apertura: string | null;
  notas: string[];
  /** El correo tal como le llega al vecino, ya renderizado por el backend. */
  html: string;
  texto: string;
  destinatarios: ResumenDestinatariosInforme;
  /** Seguro del envío real. false = no ofrecer el botón; la prueba sí. */
  envio_habilitado: boolean;
}

export interface ResultadoEnvioInforme {
  periodo: string;
  enviados: number;
  omitidos_ya_enviados: number;
  fallidos: string[];
}

export interface ResultadoEnvioPrueba {
  periodo: string;
  enviado_a: string;
  ok: boolean;
}

export function getInformeMensualVecinos(
  anio?: number,
  mes?: number,
): Promise<InformeMensualVecinosResponse> {
  const qs = new URLSearchParams();
  if (anio !== undefined) qs.set('anio', String(anio));
  if (mes !== undefined) qs.set('mes', String(mes));
  const cola = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<InformeMensualVecinosResponse>(
    `/tesoreria/reportes/informe-mensual-vecinos${cola}`,
  );
}

/**
 * Envía el informe SOLO al correo del tesorero autenticado. No lleva
 * destinatario: el backend lo resuelve de la cuenta, así no hay forma de
 * apuntar el envío a otra parte. No consume el cerrojo del envío real.
 */
export function enviarPruebaInformeMensual(
  anio?: number,
  mes?: number,
): Promise<ResultadoEnvioPrueba> {
  const qs = new URLSearchParams();
  if (anio !== undefined) qs.set('anio', String(anio));
  if (mes !== undefined) qs.set('mes', String(mes));
  const cola = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<ResultadoEnvioPrueba>(
    `/tesoreria/reportes/informe-mensual-vecinos/enviar-prueba${cola}`,
    { method: 'POST' },
  );
}

/** Envío REAL a los titulares. Idempotente por (periodo, casa). */
export function enviarInformeMensual(
  anio?: number,
  mes?: number,
): Promise<ResultadoEnvioInforme> {
  const qs = new URLSearchParams();
  if (anio !== undefined) qs.set('anio', String(anio));
  if (mes !== undefined) qs.set('mes', String(mes));
  const cola = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<ResultadoEnvioInforme>(
    `/tesoreria/reportes/informe-mensual-vecinos/enviar${cola}`,
    { method: 'POST' },
  );
}

// === Recordatorio semestral (junio y diciembre, 2026-10-08) ===

export type ModoEnvioRecordatorio = 'cerrado' | 'manual' | 'automatico';

export interface CoberturaRecordatorio {
  casas_con_saldo: number;
  con_correo: number;
  identificadores_sin_correo: string[];
  /** Casas con la marca "no avisar" de Superadmin: se respeta. */
  identificadores_sin_avisos: string[];
  ya_enviados: number;
  /** Recibieron hoy un aviso de morosidad: el recordatorio no se les repite hoy. */
  avisadas_hoy: number;
}

export interface RecordatorioSemestralResponse {
  /** Cuota que empieza: AAAA-S1 / AAAA-S2. */
  periodo: string;
  /** "1° semestre 2027" (lo arma el backend). */
  cuota_etiqueta: string;
  /** Junio o diciembre: el envío real solo se permite ahí. */
  en_temporada: boolean;
  modo_envio: ModoEnvioRecordatorio;
  dia_automatico: number;
  cobertura: CoberturaRecordatorio;
  /** null si no hay casas con saldo (no hay ejemplo que mostrar). */
  asunto: string | null;
  html: string | null;
  texto: string | null;
}

export interface ResultadoEnvioRecordatorio {
  periodo: string;
  enviados: number;
  omitidos: number;
  fallidos: string[];
}

export interface ResultadoPruebaRecordatorio {
  enviado_a: string;
  ok: boolean;
  motivo: string | null;
}

export function getRecordatorioSemestral(): Promise<RecordatorioSemestralResponse> {
  return apiFetch<RecordatorioSemestralResponse>(
    '/tesoreria/reportes/recordatorio-semestral',
  );
}

/** Prueba SOLO al correo de la cuenta autenticada; no consume el cerrojo. */
export function enviarPruebaRecordatorio(): Promise<ResultadoPruebaRecordatorio> {
  return apiFetch<ResultadoPruebaRecordatorio>(
    '/tesoreria/reportes/recordatorio-semestral/enviar-prueba',
    { method: 'POST' },
  );
}

/** Envío REAL. 409 si el seguro está cerrado o no es junio/diciembre. */
export function enviarRecordatorio(): Promise<ResultadoEnvioRecordatorio> {
  return apiFetch<ResultadoEnvioRecordatorio>(
    '/tesoreria/reportes/recordatorio-semestral/enviar',
    { method: 'POST' },
  );
}

// === Resumen anual ===

export interface ReporteResumenAnualResponse {
  anio: number;
  periodo: string;
  es_anio_en_curso: boolean;
  ingresos: number;
  gastos: number;
  resultado: number;
  saldo_cierre: number | null;
  ingresos_por_tipo: ItemComposicion[];
  gastos_por_categoria: ItemComposicion[];
}

export function getReporteResumenAnual(
  anio?: number,
  incluirComp?: boolean,
): Promise<ReporteResumenAnualResponse> {
  return apiFetch<ReporteResumenAnualResponse>(
    `/tesoreria/reportes/resumen-anual${qsAnioComp(anio, incluirComp)}`,
  );
}

export function descargarResumenAnualXlsx(
  anio?: number,
  incluirComp?: boolean,
): Promise<void> {
  return descargarArchivo(
    `/tesoreria/reportes/resumen-anual/export${qsAnioComp(anio, incluirComp)}`,
    'resumen-anual.xlsx',
  );
}

// === Flujo de caja ===

export interface MesFlujoCaja {
  mes: number;
  entradas: number;
  salidas: number;
  neto: number;
  saldo: number;
}

export interface ReporteFlujoCajaResponse {
  anio: number;
  saldo_inicial: number;
  saldo_inicial_disponible: boolean;
  saldo_final: number;
  saldo_minimo: number;
  saldo_maximo: number;
  meses: MesFlujoCaja[];
}

export function getReporteFlujoCaja(
  anio?: number,
): Promise<ReporteFlujoCajaResponse> {
  return apiFetch<ReporteFlujoCajaResponse>(
    `/tesoreria/reportes/flujo-caja${qsAnio(anio)}`,
  );
}

export function descargarFlujoCajaXlsx(anio?: number): Promise<void> {
  return descargarArchivo(
    `/tesoreria/reportes/flujo-caja/export${qsAnio(anio)}`,
    'flujo-caja.xlsx',
  );
}

// === Fondo de reserva ===

export interface MesFondoReserva {
  mes: number;
  saldo: number;
  pct_meta: number | null;
}

export interface ReporteFondoReservaResponse {
  anio: number;
  saldo_fondo: number;
  meta: number;
  pct_meta: number | null;
  gasto_mensual_promedio: number;
  meses_cobertura: number | null;
  meses: MesFondoReserva[];
}

export function getReporteFondoReserva(
  anio?: number,
): Promise<ReporteFondoReservaResponse> {
  return apiFetch<ReporteFondoReservaResponse>(
    `/tesoreria/reportes/fondo-reserva${qsAnio(anio)}`,
  );
}

export function descargarFondoReservaXlsx(anio?: number): Promise<void> {
  return descargarArchivo(
    `/tesoreria/reportes/fondo-reserva/export${qsAnio(anio)}`,
    'fondo-reserva.xlsx',
  );
}

// === Proyección 12 meses ===

export interface MesProyeccion {
  anio: number;
  mes: number;
  ingreso: number;
  ingreso_al_ritmo_actual: number;
  gasto: number;
  neto: number;
  neto_al_ritmo_actual: number;
  saldo: number;
  saldo_al_ritmo_actual: number;
}

/**
 * Proyección de caja a 12 meses con DOS escenarios (2026-09-04).
 *
 * La brecha entre "si todos pagan" y "al ritmo actual" es la morosidad, y es
 * el dato sobre el que se puede actuar. Una proyección con un solo número la
 * esconde.
 */
export interface ReporteProyeccionResponse {
  saldo_actual: number;
  saldo_actual_disponible: boolean;
  /** Si todos pagan: la emisión de cuotas del año / 12. No es un promedio. */
  ingreso_mensual_promedio: number;
  /** La misma emisión × la tasa de cobranza de las cuotas ya vencidas. */
  ingreso_mensual_al_ritmo_actual: number;
  gasto_mensual_promedio: number;
  ingreso_proyectado_total: number;
  gasto_proyectado_total: number;
  saldo_proyectado: number;
  saldo_proyectado_al_ritmo_actual: number;
  /** Procedencia de cada número, para que la pantalla pueda explicarlo. */
  emision_anual: number;
  /** null si todavía no hay cuotas vencidas: las dos líneas coinciden. */
  tasa_cobranza: number | null;
  tasa_cobranza_emitido: number;
  tasa_cobranza_cobrado: number;
  tasa_cobranza_periodos: number;
  gasto_meses_ventana: number;
  gasto_usa_historico: boolean;
  meses: MesProyeccion[];
}

export function getReporteProyeccion(): Promise<ReporteProyeccionResponse> {
  return apiFetch<ReporteProyeccionResponse>('/tesoreria/reportes/proyeccion');
}

export function descargarProyeccionXlsx(): Promise<void> {
  return descargarArchivo(
    '/tesoreria/reportes/proyeccion/export',
    'proyeccion-12-meses.xlsx',
  );
}

function qsAnio(anio?: number): string {
  return anio ? `?anio=${anio}` : '';
}

export function getReporteDeudores(
  anio?: number,
): Promise<ReporteDeudoresResponse> {
  return apiFetch<ReporteDeudoresResponse>(
    `/tesoreria/reportes/deudores${qsAnio(anio)}`,
  );
}

export function getReporteRecaudacionPorCasa(
  anio?: number,
): Promise<ReporteRecaudacionPorCasaResponse> {
  return apiFetch<ReporteRecaudacionPorCasaResponse>(
    `/tesoreria/reportes/recaudacion-por-casa${qsAnio(anio)}`,
  );
}

// === Descargas XLSX ===
// El nombre de archivo definitivo (con período) lo fija el backend vía
// Content-Disposition; el fallback solo aplica si ese header faltara.

export function descargarDeudoresXlsx(anio?: number): Promise<void> {
  return descargarArchivo(
    `/tesoreria/reportes/deudores/export${qsAnio(anio)}`,
    'deudores.xlsx',
  );
}

// Reportes reutilizados (2º corte): el export consume los services existentes
// en backend; aquí solo disparan la descarga.

function qsAnioComp(anio?: number, incluirComp?: boolean): string {
  const params = new URLSearchParams();
  if (anio) params.set('anio', String(anio));
  if (incluirComp === false) params.set('incluir_compensaciones', 'false');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function descargarIngresosGastosXlsx(
  anio?: number,
  incluirComp?: boolean,
): Promise<void> {
  return descargarArchivo(
    `/tesoreria/reportes/ingresos-gastos/export${qsAnioComp(anio, incluirComp)}`,
    'ingresos-gastos.xlsx',
  );
}

export function descargarGastosCategoriaXlsx(
  anio?: number,
  incluirComp?: boolean,
): Promise<void> {
  return descargarArchivo(
    `/tesoreria/reportes/gastos-categoria/export${qsAnioComp(anio, incluirComp)}`,
    'gastos-categoria.xlsx',
  );
}

export function descargarConciliacionXlsx(): Promise<void> {
  return descargarArchivo(
    '/tesoreria/reportes/conciliacion/export',
    'conciliacion-bancaria.xlsx',
  );
}

export function descargarCompensacionesXlsx(): Promise<void> {
  return descargarArchivo(
    '/tesoreria/reportes/compensaciones/export',
    'compensaciones.xlsx',
  );
}

export function descargarRecaudacionPorCasaXlsx(anio?: number): Promise<void> {
  return descargarArchivo(
    `/tesoreria/reportes/recaudacion-por-casa/export${qsAnio(anio)}`,
    'recaudacion-por-casa.xlsx',
  );
}
